"use client";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import Header from "@/components/global/partials/Header";
import Footer from "@/components/global/partials/Footer";
import { PageLegalSchema } from "@/sanity/pages/PageLegalTemplate";
import { motion } from "framer-motion";
import heroMobileBackground from "@/public/global/hero-mobile-background.png";
import heroDesktopBackground from "@/public/global/hero_desktop_background-lg-2.png";
import Image from "next/image";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import HeaderV2 from "../global/partials/HeaderV2";

const ease = [0.25, 0.1, 0.25, 1] as const;

const legalRichText = customRichTextComponent({
  block: {
    h2: ({ children }) => (
      <h2 className="mb-0 text-[2.25rem] font-semibold leading-[1.33] text-black/70 tracking-normal">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-0 text-[2rem] font-semibold leading-[1.33] text-black/70 tracking-normal">
        {children}
      </h3>
    ),
    normal: ({ children }) => (
      <p className="mb-0 text-[2rem] font-normal leading-[1.33] text-black/70">
        {children}
      </p>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc pl-[2.5rem] flex flex-col gap-[0.5rem]">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal pl-[2.5rem] flex flex-col gap-[0.5rem]">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="mb-0 font-geist text-[2rem] font-normal leading-[1.33] text-black/70">
        {children}
      </li>
    ),
    number: ({ children }) => (
      <li className="mb-0 font-geist text-[2rem] font-normal leading-[1.33] text-black/70">
        {children}
      </li>
    ),
  },
  types: {
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;
      return (
        <ol className="flex flex-col gap-[1.5rem] p-0 list-none">
          {items.map((item: any, index: number) => {
            const num = String(startNumber + index).padStart(2, "0");
            return (
              <li
                key={index}
                className="flex items-center gap-[1.5rem] p-0 m-0"
              >
                <div className="flex-shrink-0 border border-[rgba(0,27,20,0.1)] rounded-[0.25rem] p-[0.5rem]">
                  <div className="bg-[rgba(0,27,20,0.05)] rounded-[0.25rem] px-[0.75rem] py-[1rem] flex items-center justify-center">
                    <span className="mb-0 font-blacker italic text-[2.25rem] text-[#196551] leading-[1.15] tracking-[-0.9px] whitespace-nowrap">
                      {num}
                    </span>
                  </div>
                </div>
                <div className="richtext richtext-start prose-p:leading-[1.33] prose-p:text-black/70 prose-p:text-[2rem]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    cornerList: ({ value }) => (
      <ul className="flex flex-col items-start p-0 list-none gap-[2.5rem]">
        {value?.items?.map((item: any, index: number) => (
          <li key={index} className="flex items-center p-0 m-0 gap-[1.75rem]">
            <div className="flex-shrink-0 flex items-center justify-center w-[2.75rem] h-[2.75rem] bg-[rgba(202,223,217,0.37)] backdrop-blur-[4px] rounded-[0.125rem]">
              <div className="w-[1.219rem] h-[1.219rem] bg-[#228e71] rounded-[0.125rem]" />
            </div>
            <div className="richtext richtext-start prose-p:leading-[1.33] prose-p:text-black/70 prose-p:text-[2rem]">
              <PortableText
                value={item.content}
                components={richTextComponent}
              />
            </div>
          </li>
        ))}
      </ul>
    ),
  },
});

export default function PageLegalJSX({ data }: { data: PageLegalSchema }) {
  const { title, blocksText } = data;

  return (
    <main className="flex flex-col items-center">
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={"main"} // showBorder
          language={data.settings?.language}
        />
      )}

      {/* Hero */}
      <section className="relative overflow-x-clip w-full bg-white pt-[7rem] pb-[4rem] lg:pt-[12rem] lg:pb-[6rem]">
        <motion.div
          initial={{ opacity: 0, filter: "blur(8px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-[200%] top-[0rem] lg:hidden block z-0 pointer-events-none"
        >
          <Image
            src={heroMobileBackground}
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, filter: "blur(8px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-[130rem] top-[0rem] hidden lg:block z-0 pointer-events-none"
        >
          <Image
            src={heroDesktopBackground}
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <div className="mx-auto relative z-10 w-[min(141rem,calc(100%-4rem))] lg:w-[min(141rem,calc(100%-8rem))]">
          {title && (
            <motion.div
              className="richtext prose-headings:lg:text-[7rem] prose-headings:text-[4.5rem] "
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
            >
              <PortableText value={title} components={richTextComponent} />
            </motion.div>
          )}
        </div>
      </section>

      {/* Content card */}
      {!!blocksText?.length && (
        <section className="relative w-full pb-[8rem] lg:pb-[12.5rem]">
          <div className="mx-auto w-[min(82.625rem,calc(100%-4rem))] lg:w-[min(82.625rem,calc(100%-8rem))]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.9, ease }}
              className="bg-white border border-[#e5e5e5] rounded-[0.25rem] p-[4rem] lg:p-[5rem]"
            >
              {blocksText.map((block, index) => (
                <div key={block._key ?? index}>
                  {index > 0 && (
                    <div className="w-full h-px bg-[#e5e5e5] my-[4rem]" />
                  )}
                  <div className="richtext richtext-start flex flex-col gap-[3rem]">
                    <PortableText
                      value={block.text}
                      components={legalRichText}
                    />
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      <ScrollingBanner
        className="bg-[#f4f5f5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />

      {data?.settings?.footer && <Footer data={data.settings?.footer} finalCta={data} language={data?.settings?.language} />}
    </main>
  );
}
